import { Timestamp } from 'firebase/firestore';
import { addDays, format } from 'date-fns';
import { findTeamVacationOverlaps } from '@/features/Vacations/utils/vacationTeamOverlap';
import type { ClientVacationGroup } from '@/features/Vacations/models/vacationClientView.types';
import type { VacationDpRow } from '@/features/Vacations/models/vacationDpBoard.types';
import type { VacationRequestStatus } from '@/features/Vacations/models/vacation.types';
import { APPROVAL_LEAD_DAYS } from '@/features/Vacations/utils/vacationApprovalDeadline';

const CLIENT_NAME = 'Cliente Exemplo';
const RESPONSIBLE = { responsibleId: 'demo-conflict-responsible', responsibleName: 'Responsável Exemplo' };

const iso = (date: Date) => format(date, 'yyyy-MM-dd');

function firstMondayOnOrAfter(base: Date, offsetDays: number): Date {
    const date = addDays(base, offsetDays);
    return addDays(date, (8 - date.getDay()) % 7);
}

function buildRow(
    collaboratorId: string,
    name: string,
    request: { id: string; status: VacationRequestStatus; start: Date; end: Date } | null,
    approved: { requestId: string; start: Date; end: Date } | null
): VacationDpRow {
    const days = (start: Date, end: Date) => Math.round((end.getTime() - start.getTime()) / 86400000) + 1;

    return {
        collaboratorId,
        collaboratorName: name,
        collaboratorEmail: `${name.toLowerCase().split(' ')[0]}@rarolabs.com.br`,
        collaboratorRole: 'Analista',
        collaboratorSquad: 'Squad Exemplo',
        clientNames: [CLIENT_NAME],
        clientResponsibles: [{ clientName: CLIENT_NAME, ...RESPONSIBLE }],
        approvedPeriods: approved
            ? [
                  {
                      requestId: approved.requestId,
                      status: 'Scheduled',
                      startDate: iso(approved.start),
                      endDate: iso(approved.end),
                      days: days(approved.start, approved.end),
                      hasApprovedSell: false,
                      segmentOrder: 1,
                      segmentCount: 1,
                      modality: '30',
                      requestedAt: iso(addDays(approved.start, -60)),
                  },
              ]
            : [],
        request: request
            ? ({
                  id: request.id,
                  collaboratorId,
                  collaboratorName: name,
                  status: request.status,
                  modality: '30',
                  totalDays: days(request.start, request.end),
                  segments: [
                      {
                          order: 1,
                          startDate: iso(request.start),
                          endDate: iso(request.end),
                          days: days(request.start, request.end),
                          approved: null,
                      },
                  ],
                  requestedAt: Timestamp.now(),
                  sellRequest: { requested: false, days: 0, status: 'None' },
                  conflictsSnapshot: [],
              } as unknown as VacationDpRow['request'])
            : null,
        balanceDays: 30,
        expiredDays: 0,
        pastLimitDays: 0,
        daysWithinDeadline: 30,
        scheduledDays: 0,
        pendingDays: 0,
        concessiveDeadline: null,
        legalDeadline: null,
        deadlineIsProjected: false,
        approvalDeadline: null,
        legacyTakenDays: 0,
        legacyScheduledDays: 0,
        monthsWorked: 24,
        unallocatedDays: 0,
        eligible: true,
    } as unknown as VacationDpRow;
}

export function buildConflictsDemoGroups(today: Date): ClientVacationGroup[] {
    const start = firstMondayOnOrAfter(today, APPROVAL_LEAD_DAYS + 10);
    const at = (offset: number) => addDays(start, offset);

    const rows = [
        buildRow('demo-conflict-ana', 'Ana Exemplo', { id: 'demo-conflict-ana-req', status: 'PendingDP', start: at(0), end: at(14) }, null),
        buildRow('demo-conflict-bruno', 'Bruno Exemplo', { id: 'demo-conflict-bruno-req', status: 'PendingManager', start: at(10), end: at(24) }, null),
        buildRow('demo-conflict-carla', 'Carla Exemplo', { id: 'demo-conflict-carla-req', status: 'PendingManager', start: at(18), end: at(21) }, null),
        buildRow('demo-conflict-diego', 'Diego Exemplo', null, { requestId: 'demo-conflict-diego-req', start: at(3), end: at(12) }),
    ];

    return findTeamVacationOverlaps(rows);
}

export function summarizeDemoGroups(groups: ClientVacationGroup[]) {
    const clientCount = groups.length;
    const peopleCount = new Set(
        groups.flatMap((group) => group.responsibles.flatMap((r) => r.periods.map((p) => p.collaboratorId)))
    ).size;
    const overlapCount = groups.reduce((sum, group) => sum + group.overlapCount, 0);
    return { clientCount, peopleCount, overlapCount };
}
